import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { composeUrl } from "../../utils/api";
import { getClaimValue } from "../../utils/authUser";
import LimeClaimTypes from "../../models/limeClaimTypes";
import LoadingSpinner from "../Shared/LoadingSpinner/LoadingSpinner";
import Mail from "../../images/mail-02.svg";
import Trash from "../../images/trash-01.svg";
import Edit from "../../images/edit-pencil.svg";
import { getFeatureFlag } from "../../utils/featureFlag";
import ConfirmDeleteMemberModal from "./ConfirmDeleteMemberModal";
import EditMemberModal from "./EditMemberModal";
import { MemberStatusId } from "../Enum/MemberStatus";
import {
    portalCardSurface,
    portalOutlineControl,
    portalPageSubtitle,
    portalTableBodyCell,
    portalTableBodyRow,
    portalTableCard,
    portalTableHeadCell,
} from "../../theme/portalLayout";

export interface MemberSummary {
    id: number;
    statusId: number;
    statusName: string;
    startDate: string;
    endDate?: string;
    externalId: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface MemberStats {
    eligibleMembersCount: number;
    enrolledMembersCount: number;
    optedOutMembersCount: number;
    agreedMembersCount: number;
}

function MemberSearch() {
    const auth = useAuth();
    const [members, setMembers] = useState<MemberSummary[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [resendMemberInviteEnabled, setResendMemberInviteEnabled] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState<MemberSummary | null>(null);
    const [deleteError, setDeleteError] = useState("");
    const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState<MemberSummary | null>(null);
    const [editMessage, setEditMessage] = useState("");
    const [editError, setEditError] = useState("");
    const [emailMessage, setEmailMessage] = useState("");
    const [emailError, setEmailError] = useState("");

    const fetchMembers = useCallback(
        async (query: string = "") => {
            try {
                setLoading(true);
                setError("");
                const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
                const url = composeUrl(
                    `organisations/${orgUuid}/members/filteredMembers?search=${encodeURIComponent(
                        query
                    )}&page=${page}&pageSize=${pageSize}`
                );
                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${auth.user?.access_token}`,
                        "Content-Type": "application/json",
                    },
                });
                if (!response.ok) {
                    setError("Unable to fetch members");
                    return;
                }
                const data: MemberSummary[] = await response.json();
                setMembers(data);
            } catch {
                setError("An error occurred while fetching members");
            } finally {
                setLoading(false);
            }
        },
        [auth, page, pageSize]
    );

    const fetchMemberStats = useCallback(async () => {
        try {
            const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
            const statsUrl = composeUrl(`organisations/${orgUuid}/dashboard/memberStats`);
            const response = await fetch(statsUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) return;
            const data: MemberStats = await response.json();
            setMemberStats(data);
        } catch {
            console.error("Error occurred while fetching member stats");
        }
    }, [auth]);

    const getLimitColorClass = (eligible: number, agreed: number) => {
        if (agreed === 0) return "text-error-red";
        const ratio = (eligible / agreed) * 100;
        if (ratio >= 100) return "text-error-red";
        if (ratio >= 90) return "text-lime-orange";
        return "text-primary-text";
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchMembers(searchQuery);
        }, 800); // debounce input
        return () => clearTimeout(timeoutId);
    }, [searchQuery, page, pageSize, fetchMembers]);

    useEffect(() => {
        fetchMemberStats();
        (async () => {
            const featureFlags = await getFeatureFlag(auth);
            setResendMemberInviteEnabled(featureFlags.resendMemberInviteEnabled);
        })();
    }, [auth, fetchMemberStats]);

    async function handleDeleteConfirmed() {
        if (!memberToDelete) return;
        try {
            setDeleteError("");
            setLoading(true);
            const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
            const response = await fetch(composeUrl(`organisations/${orgUuid}/members/${memberToDelete.externalId}`), {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                setDeleteError("Unable to delete member");
                return;
            }
            setMembers((prev) =>
                prev.map((m) =>
                    m.externalId === memberToDelete.externalId ? { ...m, statusName: "Removed" } : m
                )
            );
        } catch {
            setDeleteError("An error occurred while deleting the member");
        } finally {
            setLoading(false);
            setMemberToDelete(null);
        }
    }

    async function handleResendWelcomeEmail(member: MemberSummary) {
        if (!member) return;
        try {
            setEmailError("");
            setEmailMessage("");
            setLoading(true);
            const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
            const payload = {
                externalUserId: member.externalId.trim(),
                email: member.email.trim()
            };
            const response = await fetch(composeUrl(`organisations/${orgUuid}/members/resendEmail`), {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const responseBody = await response.json().catch(() => null);
                const msg = responseBody?.message || "An error occurred while sending the member email.";
                setEmailError(msg);
                return;
            }
            setEmailMessage("Welcome email resent.");
        } catch {
            setEmailError("An unknown error occurred while sending the member email.");
        } finally {
            setLoading(false);
        }
    }
    
    function handleOpenDeleteModal(member: MemberSummary) {
        setDeleteError("");
        setMemberToDelete(member);
    }

    function handleOpenEditModal(member: MemberSummary) {
        setMemberToEdit(member);
        setEditModalOpen(true);
        setEditMessage("");
        setEditError("");
    }

    function handleEditSuccess() {
        setEditModalOpen(false);
        setMemberToEdit(null);
        setTimeout(() => {
            fetchMembers(searchQuery);
        }, 200);
        setEditMessage("Member updated successfully.");
        setEditError("");
    }

    function handleEditFailure() {
        setEditModalOpen(true);
        setEditMessage("");
        setEditError("An error occurred while updating the member.");
    }

    return (
        <LoadingSpinner loading={loading}>
            {error && <p className="text-error">{error}</p>}
            {editMessage && <p className="text-green-600">{editMessage}</p>}
            {editError && <p className="text-error-red">{editError}</p>}
            {emailMessage && <p className="text-green-600">{emailMessage}</p>}
            {emailError && <p className="text-error-red">{emailError}</p>}

            <ConfirmDeleteMemberModal
                show={!!memberToDelete}
                member={memberToDelete ?? undefined}
                onConfirm={handleDeleteConfirmed}
                onCancel={() => setMemberToDelete(null)}
                errorMessage={deleteError}
            />

            <EditMemberModal
                isOpen={editModalOpen}
                setIsOpen={setEditModalOpen}
                member={memberToEdit}
                onEditSuccess={handleEditSuccess}
                onEditFailure={handleEditFailure}
                setLoading={setLoading}
            />

            <div className="w-full flex flex-col gap-md lg:flex-row lg:justify-between lg:items-start my-4">
                {memberStats && (
                    <div className={`p-4 lg:p-6 shrink-0 ${portalCardSurface}`}>
                        <div className="lg:min-w-[8rem]">
                            <h3 className={`lg:text-h3 mb-2 ${getLimitColorClass(
                                memberStats.eligibleMembersCount,
                                memberStats.agreedMembersCount
                            )}`}>
                                {String(memberStats.eligibleMembersCount).padStart(2, "0")}/
                                {String(memberStats.agreedMembersCount).padStart(2, "0")}
                            </h3>
                            <p className="text-p2 font-medium text-portal-teal whitespace-normal lg:whitespace-nowrap">
                                Eligible / Agreed Members
                            </p>
                        </div>
                    </div>
                )}
                <div className="w-full flex flex-row justify-stretch lg:justify-end lg:my-0 my-2">
                    <input
                        type="text"
                        placeholder="Start typing your search criteria..."
                        className={`w-full lg:w-3/4 xl:w-1/2 ${portalOutlineControl}`}
                        value={searchQuery}
                        onChange={(e) => {
                            setPage(1);
                            setSearchQuery(e.target.value);
                        }}
                    />
                </div>
            </div>

            {members.length === 0 && searchQuery === "" ? (
                <div className={`text-center py-md px-md ${portalCardSurface}`}>
                    <p className={portalPageSubtitle}>
                        You don&apos;t have any members yet. Upload a CSV file or create users individually.
                    </p>
                </div>
            ) : (
                <>
                    <div className={portalTableCard}>
                    <table className="w-full">
                        <thead>
                        <tr className="h-2xl">
                            <th className={portalTableHeadCell}>Name</th>
                            <th className={portalTableHeadCell}>Email</th>
                            <th className={portalTableHeadCell}>Status</th>
                            <th className={portalTableHeadCell}>Employee ID</th>
                            <th className={portalTableHeadCell} />
                        </tr>
                        </thead>
                        <tbody>
                            {members.length > 0 && members.map((member) => (
                                <tr key={member.id} className={`h-2xl ${portalTableBodyRow}`}>
                                    <td className={portalTableBodyCell}>
                                        {member.firstName} {member.lastName}
                                    </td>
                                    <td className={portalTableBodyCell}>{member.email}</td>
                                    <td className={portalTableBodyCell}>{member.statusName}</td>
                                    <td className={portalTableBodyCell}>{member.externalId}</td>
                                    <td className={portalTableBodyCell}>
                                        <div className="w-full hidden lg:flex items-end space-x-4">
                                            {member.statusName === "Invited" && resendMemberInviteEnabled && (
                                                <img
                                                    className="hover:cursor-pointer"
                                                    src={Mail}
                                                    alt="Resend welcome email"
                                                    onClick={() => handleResendWelcomeEmail(member)}
                                                />
                                            )}
                                            {(member.statusId !== MemberStatusId.OptedOut && member.statusId !== MemberStatusId.Removed) && (
                                                <img
                                                    className="hover:cursor-pointer"
                                                    src={Edit}
                                                    alt="Edit user"
                                                    onClick={() => handleOpenEditModal(member)}
                                                />
                                            )}
                                            {member.statusId !== MemberStatusId.Removed && (
                                                <img
                                                    className="hover:cursor-pointer"
                                                    src={Trash}
                                                    alt="Delete user"
                                                    onClick={() => handleOpenDeleteModal(member)}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <div className="flex justify-between items-center mt-4 mb-5 text-portal-teal">
                        <div>
                            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                                Previous
                            </button>
                            <span className="mx-2">Page {page}</span>
                            <button disabled={members.length < pageSize} onClick={() => setPage(page + 1)}>
                                Next
                            </button>
                        </div>
                        <div>
                            <label htmlFor="pageSize">Page Size: </label>
                            <select
                                id="pageSize"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                    </div>
                </>
            )}
        </LoadingSpinner>
    );
}

export default MemberSearch;